import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { trackButtonClick, trackAssessmentStart, trackExternalLink } from '../services/analytics';
import GoogleSignInButton from '../components/GoogleSignInButton';
import Navbar from '../components/Navbar';
import { useAuth } from '../firebase/AuthContext';
import { 
  HiPlay, 
  HiUserGroup, 
  HiClock,
  HiLockClosed,
  HiChartBar,
  HiLightBulb,
  HiAcademicCap,
  HiCheckCircle,
  HiChevronRight
} from 'react-icons/hi';
import { FaInstagram, FaTwitter, FaYoutube, FaRocket, FaTrophy, FaPoll } from 'react-icons/fa';
import FeedbackForm from '../components/FeedbackForm';
import LevelProgressPath from '../components/LevelProgressPath';
import { getLevelIcon } from '../components/LevelIcons';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, backendUserId, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    total_users: 0,
    average_score: 0,
    highest_level: 0,
    highest_level_name: '',
    users_at_highest: 0,
    best_performance: null,
    level_distribution: {}
  });
  const [userStats, setUserStats] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showPollingLoginModal, setShowPollingLoginModal] = useState(false);
  const loadedUserIdRef = useRef(null);

  // Check if redirected from Polling Zone for login
  // Wait for auth to complete loading before showing modal
  useEffect(() => {
    if (location.state?.showPollingLogin && !authLoading) {
      // Small delay to ensure page has rendered
      const timer = setTimeout(() => {
        setShowPollingLoginModal(true);
      }, 100);
      // Clear the state without changing the URL
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location.state, authLoading]);

  // Redirect to polling zone after login if modal was shown
  useEffect(() => {
    if (showPollingLoginModal && user) {
      setShowPollingLoginModal(false);
      navigate('/polling');
    }
  }, [user, showPollingLoginModal, navigate]);

  const levelData = [
    { level: 0, name: 'AI Noob', color: '#F3E8FF', description: 'Pre-level status' },
    { level: 1, name: 'AI Novice', color: '#E9D5FF', description: 'Learning the basics' },
    { level: 2, name: 'AI Explorer', color: '#D6BCFA', description: 'Building foundation' },
    { level: 3, name: 'AI Practitioner', color: '#B794F4', description: 'Discovering AI concepts' },
    { level: 4, name: 'AI Specialist', color: '#9F7AEA', description: 'Applying AI knowledge' },
    { level: 5, name: 'AI Expert', color: '#7C3AED', description: 'Deep expertise' },
    { level: 6, name: 'AI Master', color: '#6D28D9', description: 'Mastering concepts' },
    { level: 7, name: 'AI Guru', color: '#4C1D95', description: 'AI wisdom achieved' }
  ];

  // ✅ FIXED: Calculate stats from actual test history instead of using stale localStorage
  const calculateUserStatsFromHistory = useCallback((tests) => {
    if (!tests || tests.length === 0) {
      return {
        total_tests_taken: 0,
        best_level: 0,
        best_level_name: 'Not Started',
        average_accuracy: 0,
        total_questions_answered: 0
      };
    }
    
    const totalTests = tests.length;
    
    // Find best test (highest level achieved)
    const bestTest = tests.reduce((best, current) => 
      current.final_level > best.final_level ? current : best
    , tests[0]);
    
    // Calculate total correct answers and total questions
    let totalCorrect = 0;
    let totalQuestions = 0;
    
    tests.forEach(test => {
      totalCorrect += test.total_correct || 0;
      totalQuestions += test.total_questions || 0;
    });
    
    const averageAccuracy = totalQuestions > 0 
      ? (totalCorrect / totalQuestions) * 100 
      : 0;
    
    const bestLevelInfo = levelData.find(l => l.level === bestTest.final_level) || levelData[0];
    
    return {
      total_tests_taken: totalTests,
      best_level: bestTest.final_level || 0,
      best_level_name: bestLevelInfo.name,
      average_accuracy: averageAccuracy,
      total_questions_answered: totalQuestions
    };
  }, [levelData]);

  // ✅ FIXED: Load user data from API response, not localStorage
  const loadUserData = useCallback(async () => {
    if (!backendUserId || loadedUserIdRef.current === backendUserId) return;
    
    try {
      console.log('📊 Loading user data for:', backendUserId);
      loadedUserIdRef.current = backendUserId;
      
      const response = await apiService.getTestHistory(backendUserId, 20);
      console.log('🔍 DEBUG - Full API Response:', response);
      
      if (response.success) {
        const tests = response.tests || [];
        setUserHistory(tests);
        
        console.log('🔍 DEBUG - Tests count:', tests.length);
        console.log('🔍 DEBUG - Tests data:', tests);
        
        // ✅ Use stats from API if available, otherwise calculate from history
        if (response.stats) {
          console.log('✅ Using stats from API:', response.stats);
          setUserStats(response.stats);
          // Update localStorage cache
          localStorage.setItem('user_stats', JSON.stringify(response.stats));
        } else {
          // ✅ Calculate stats from test history
          const calculatedStats = calculateUserStatsFromHistory(tests);
          console.log('✅ Calculated stats from history:', calculatedStats);
          setUserStats(calculatedStats);
          // Cache the calculated stats
          localStorage.setItem('user_stats', JSON.stringify(calculatedStats));
        }
      }
    } catch (error) {
      console.error('❌ Failed to load user data:', error);
      // On error, try to use cached data as fallback
      const cachedStats = localStorage.getItem('user_stats');
      if (cachedStats) {
        console.log('⚠️ Using cached stats as fallback');
        setUserStats(JSON.parse(cachedStats));
      }
    }
  }, [backendUserId, calculateUserStatsFromHistory]);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (user && backendUserId) {
      // ✅ Clear the ref when user changes to force reload
      loadedUserIdRef.current = null;
      loadUserData();
    }
  }, [user, backendUserId, loadUserData]);

  // ✅ Reload user data when returning from results page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user && backendUserId) {
        console.log('👀 Page became visible, reloading user data');
        loadedUserIdRef.current = null;
        loadUserData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, backendUserId, loadUserData]);

  const loadStats = async () => {
    try {
      const data = await apiService.getStats();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section id="home" className="bg-gradient-to-b from-white to-gray-50 flex items-center justify-center pt-20 sm:pt-24 md:pt-0 pb-8 md:pb-0 mb-0 md:-mb-8" style={{ minHeight: '85vh', height: 'auto' }}>
        <div className="max-w-7xl w-full px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
          <div className="max-w-4xl w-full mx-auto text-center flex flex-col items-center justify-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              Discover Your AI Level
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed">
              <b>Stop guessing</b> your AI knowledge level. 
              <br></br>Know your level in minutes, from <b>AI Noob to AI Guru</b>.
            </p>

            {/* Animated level progress path */}
            <LevelProgressPath
              levelData={levelData}
              currentLevel={userStats?.best_level || 0}
            />

            {/* CTA Button */}
            <button 
              onClick={() => {
                trackButtonClick('start_assessment', 'hero_section');
                trackAssessmentStart(!user);
                navigate('/progress', { state: { currentLevel: 1, completedLevels: [] } });
              }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white text-lg font-semibold rounded-lg hover:bg-primary-dark transition-colors shadow-md hover:shadow-lg"
            >
              <HiPlay className="text-lg" />
              <span>{user ? 'Start Assessment' : 'Start Assessment Anonymously'}</span>
            </button>

            {/* Privacy Badge */}
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
              <HiLockClosed className="text-sm" />
              <span>Login is optional</span>
            </div>
            
            {user && (
              <div className="mt-6">
                <GoogleSignInButton variant="signout" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Polling Zone Feature Card */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            onClick={() => {
              trackButtonClick('polling_zone', 'feature_card');
              navigate('/polling');
            }}
            className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl shadow-2xl p-8 md:p-12 cursor-pointer transform transition-all hover:scale-[1.02] hover:shadow-3xl"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1 text-white">
                <h2 className="text-3xl md:text-4xl font-extrabold mb-3 flex items-center gap-3">
                  <FaPoll className="text-3xl" />
                  Polling Zone
                </h2>
                <p className="text-lg md:text-xl text-purple-100 mb-4 leading-relaxed">
                  Share your opinion on trending AI topics and see what the community thinks
                </p>
                <div className="flex items-center gap-4 text-sm text-purple-200">
                  <span className="flex items-center gap-2">
                    <HiChartBar className="text-base" />
                    Real-time results
                  </span>
                  <span className="flex items-center gap-2">
                    <HiUserGroup className="text-base" />
                    Community insights
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-bold rounded-lg hover:bg-purple-50 transition-colors shadow-lg text-lg">
                  Vote Now
                  <HiChevronRight className="text-xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Stats Section - Logged in users only */}
      {user && backendUserId && (
        <section id="journey" className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-2">
              <HiUserGroup className="text-primary text-2xl" />
              Your AI Journey
            </h2>
            {userStats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                      <FaRocket className="text-white text-lg" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{userStats.total_tests_taken || 0}</div>
                  <div className="text-sm text-gray-600">Tests Taken</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-pink-500 rounded-lg flex items-center justify-center mb-4">
                    <FaTrophy className="text-white text-lg" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{userStats.best_level || 0}</div>
                  <div className="text-sm text-gray-600">{userStats.best_level_name || 'Not Started'}</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                    <HiCheckCircle className="text-white text-lg" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {userStats.average_accuracy ? `${userStats.average_accuracy.toFixed(1)}%` : '0%'}
                  </div>
                  <div className="text-sm text-gray-600">Average Accuracy</div>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                    <HiLightBulb className="text-white text-lg" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{userStats.total_questions_answered || 0}</div>
                  <div className="text-sm text-gray-600">Questions Answered</div>
                </div>
              </div>
            )}
            {/* Recent Tests */}
            {userHistory.length > 0 && (
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <HiClock className="text-primary text-xl" />
                  Recent Tests
                </h3>
                <div className="space-y-4">
                  {userHistory.map((test, index) => {
                    const levelInfo = levelData.find(l => l.level === test.final_level) || levelData[0];
                    const LevelIcon = getLevelIcon(levelInfo.level, levelInfo.name) || HiAcademicCap;
                    return (
                      <div 
                        key={test.test_id || index}
                        className={`bg-white rounded-lg p-5 md:p-6 border hover:shadow-md transition-shadow ${
                          test.is_best ? 'border-yellow-400 border-2' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div 
                            className="w-14 h-14 rounded-lg flex items-center justify-center text-white text-xl flex-shrink-0"
                            style={{ backgroundColor: levelInfo.color }}
                          >
                            <LevelIcon />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-1">
                              {test.level_name || levelInfo.name}
                              {test.is_best && <FaTrophy className="text-yellow-500" />}
                            </div>
                            <div className="text-sm text-gray-500 mb-2">
                              {new Date(test.completed_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <HiCheckCircle className="text-green-600" />
                                {test.total_correct}/{test.total_questions} correct
                              </span>
                              <span className="flex items-center gap-1">
                                <HiClock className="text-primary" />
                                {Math.floor(test.total_time_taken / 60)}m {test.total_time_taken % 60}s
                              </span>
                            </div>
                          </div>
                          <div className="text-3xl md:text-4xl font-bold self-start md:self-center" style={{ color: levelInfo.color }}>
                            {test.final_level}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* No Tests Yet */}
            {userHistory.length === 0 && (
              <div className="bg-white rounded-lg p-8 md:p-12 text-center border border-gray-200">
                <HiAcademicCap className="text-4xl md:text-5xl text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-600 mb-6">
                  You haven&apos;t taken any tests yet. Start your AI journey now!
                </p>
                <button 
                  onClick={() => {
                    trackButtonClick('take_first_test', 'journey_section');
                    trackAssessmentStart(!user);
                    navigate('/progress', { state: { currentLevel: 1, completedLevels: [] } });
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white text-base font-semibold rounded-lg hover:bg-primary-dark transition-colors shadow-md"
                >
                  <HiPlay className="text-lg" />
                  <span>Take Your First Test</span>
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Login Prompt for Anonymous Users - Progress */}
      {!user && (
        <section id="progress" className="bg-white border-b border-gray-100 py-16 md:py-20 flex items-center justify-center" style={{ minHeight: '60vh' }}>
          <div className="max-w-2xl w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
            <div className="border-2 border-primary/30 rounded-xl bg-white p-8 shadow-sm w-full flex flex-col items-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Track Your Progress
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                <span className="block mb-2">You shouldn't have to start from scratch each time. 
                  <br></br><b>Sign in</b> to save your results and track your improvement.</span>
                <span className="block">Stay anonymous if you want, your score won't be saved, only added to community stats.</span>
              </p>
              <div className="flex justify-center">
                <GoogleSignInButton variant="primary" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Community Stats Section */}
      <section id="stats" className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 md:mb-12 text-center">Community Stats</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
            <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <HiUserGroup className="text-white text-xl" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {stats.level_distribution ? Object.values(stats.level_distribution).reduce((sum, count) => sum + count, 0) : 0}
              </div>
              <div className="text-gray-600 font-medium">Total Attempts</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 text-center hover:shadow-md transition-shadow ">
              <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <HiChartBar className="text-white text-xl" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {stats.level_distribution ? (() => {
                  let totalScore = 0;
                  let totalUsers = 0;
                  Object.entries(stats.level_distribution).forEach(([level, count]) => {
                    totalScore += parseInt(level) * count;
                    totalUsers += count;
                  });
                  return totalUsers > 0 ? (totalScore / totalUsers).toFixed(1) : 0;
                })() : 0}
              </div>
              <div className="text-gray-600 font-medium group relative inline-block cursor-help">
                Average Level
                <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-48 rounded-lg bg-gray-800 px-3 py-2 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-10 text-center">
                  Level scale: 0 (AI Noob) to 7 (AI Guru)
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg border border-gray-300 p-6 md:p-8 text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTrophy className="text-white text-xl" />
              </div>
              {stats.best_performance && stats.best_performance.total_correct > 0 ? (
                <>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
                    Level {stats.best_performance.final_level}
                  </div>
                  <div className="text-lg text-gray-700 mb-1">
                    {stats.best_performance.total_correct}/{stats.best_performance.total_questions} correct
                  </div>
                  <div className="text-sm text-gray-500 mb-1">
                    Time: {Math.floor(stats.best_performance.time_taken / 60)}m {stats.best_performance.time_taken % 60}s
                  </div>
                  <div className="text-sm text-gray-500 mt-1">by {stats.best_performance.achieved_by || 'Anonymous'}</div>
                  <div className="text-gray-600 font-medium">Best Performance Overall</div>
                </>
              ) : (
                <>
                  <div className="text-2xl text-gray-400 mb-2">No data yet</div>
                  <div className="text-gray-600 font-medium">Best Score</div>
                </>
              )}
            </div>
          </div>

          {/* Level Distribution Chart */}
          {stats.level_distribution && Object.keys(stats.level_distribution).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-6 md:p-8 border border-gray-200">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Times Each Level Achieved</h3>
              <div className="max-w-4xl mx-auto">
                <ResponsiveContainer width="100%" height={550}>
                  <BarChart
                    data={levelData.map((item) => ({
                      name: `${item.level} - ${item.name}`,
                      value: stats.level_distribution[item.level] || 0,
                      color: item.color
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-35} 
                      textAnchor="end" 
                      interval={0} 
                      height={100}
                      tick={{ fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => `${value} attempts`}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="value">
                      {levelData.map((item, index) => (
                        <Cell key={`cell-${index}`} fill={item.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how" className="bg-gray-50 flex items-center justify-center border-b border-gray-100" style={{ minHeight: '80vh', marginBottom: '-2rem' }}>
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 text-center break-words">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full mb-4">
            <div className="flex flex-col items-center bg-white rounded-lg p-6 border border-gray-200 shadow-sm h-full">
              <HiAcademicCap className="text-primary text-3xl mb-2" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Start the Journey</h3>
              <ul className="text-base md:text-lg text-gray-600 leading-relaxed text-left list-disc pl-4 w-full">
                <li>Begin at Level 1 with foundational AI questions</li>
                <li>Each level is timed (4 minutes per level)</li>
                <li>Adaptive difficulty as you progress</li>
              </ul>
            </div>
            <div className="flex flex-col items-center bg-white rounded-lg p-6 border border-gray-200 shadow-sm h-full">
              <HiCheckCircle className="text-primary text-3xl mb-2" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Answer & Progress</h3>
              <ul className="text-base md:text-lg text-gray-600 leading-relaxed text-left list-disc pl-4 w-full">
                <li>Answer multiple-choice questions per level</li>
                <li>Score 80%+ to unlock the next level</li>
                <li>Instant feedback after each level</li>
              </ul>
            </div>
            <div className="flex flex-col items-center bg-white rounded-lg p-6 border border-gray-200 shadow-sm h-full">
              <HiChartBar className="text-primary text-3xl mb-2" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Track Your Growth</h3>
              <ul className="text-base md:text-lg text-gray-600 leading-relaxed text-left list-disc pl-4 w-full">
                <li>See your level, accuracy, and speed stats</li>
                <li>Compare your progress with the community</li>
                <li>Review your test history and best scores</li>
              </ul>
            </div>
            <div className="flex flex-col items-center bg-white rounded-lg p-6 border border-gray-200 shadow-sm h-full">
              <HiLightBulb className="text-primary text-3xl mb-2" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Get Personalized Insights</h3>
              <ul className="text-base md:text-lg text-gray-600 leading-relaxed text-left list-disc pl-4 w-full">
                <li>Receive AI-powered study recommendations</li>
                <li>Identify your strengths and improvement areas</li>
                <li>Access curated resources for next steps</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white flex items-center justify-center" style={{ minHeight: '80vh', paddingTop: 0, paddingBottom: 0, marginBottom: '-2rem' }}>
        <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8 break-words mt-16 sm:mt-0">Why Educado AI?</h2>
          <div className="flex flex-col md:flex-row gap-6 w-full justify-center items-stretch">
            <div className="flex-1 flex flex-col items-center border-2 border-red-200 rounded-xl bg-red-50 p-6 shadow-sm min-h-full">
              <div className="w-14 h-14 bg-red-500 rounded-full flex items-center justify-center mb-4">
                <span className="text-white text-2xl">❓</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">The Problem</h3>
              <p className="text-gray-600 mb-0">You're trying to be AI-ready, but you don't know where you stand today.</p>
            </div>

            <div className="flex-1 flex flex-col items-center border-2 border-blue-200 rounded-xl bg-blue-50 p-6 shadow-sm min-h-full">
              <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                <span className="text-white text-2xl">💡</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">The Solution</h3>
              <p className="text-gray-600 mb-0">A quick test that gives you a clear starting point.</p>
            </div>

            <div className="flex-1 flex flex-col items-center border-2 border-green-200 rounded-xl bg-green-50 p-6 shadow-sm min-h-full">
              <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <span className="text-white text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">The Result</h3>
              <p className="text-gray-600 mb-0">Your level (0–7), your weak spots, and a way to track your growth over time.</p>
            </div>
          </div>

          {/* Feedback Section */}
          <div className="w-full mt-8">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 mb-8 max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Send Us Feedback</h3>
              <p className="text-gray-600 mb-4">Share your experience — feedback helps us improve the assessment and recommendations.</p>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    trackButtonClick('give_feedback', 'home_page');
                    setShowFeedbackModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-base font-semibold rounded-lg hover:bg-primary-dark transition-colors shadow-md"
                >
                  Give Feedback
                </button>
              </div>
              {showFeedbackModal && (
                <FeedbackForm modal onClose={() => setShowFeedbackModal(false)} autoFocus />
              )}
            </div>
          </div>

          {/* Follow Us on Social Media Section */}
          <section className="w-full flex flex-col items-center justify-center py-12 bg-white">
            <h4 className="text-2xl font-bold text-gray-900 mb-2">
              👋 Follow <span className="text-primary">Educado AI</span> on Social Media!
            </h4>
            <p className="text-gray-600 text-base mb-6">
              Stay updated with AI learning tips, new features, and more!
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a 
                href="https://www.linkedin.com/company/educado-ai/?utm_source=home_page&utm_medium=social&utm_campaign=follow" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => trackExternalLink('https://www.linkedin.com/company/educado-ai/', 'linkedin_home')}
                className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-white bg-[#0A66C2] rounded-lg hover:bg-[#004182] transition-all text-sm"
              >
                <span className="text-lg">💼</span>
                <span>LinkedIn</span>
              </a>
              <a 
                href="https://www.instagram.com/educadoai?utm_source=home_page&utm_medium=social&utm_campaign=follow" 
                target="_blank" 
                rel="noopener noreferrer" 
                onClick={() => trackExternalLink('https://www.instagram.com/educadoai', 'instagram_home')}
                className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg hover:shadow-md transition-all text-sm"
              >
                <span className="text-lg">📸</span>
                <span>Instagram</span>
              </a>
            </div>
          </section>
        </div>
      </section>

      {/* Footer */}
      <div className="pb-16"></div>
      <footer className="bg-gray-900 py-3 fixed bottom-0 left-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-400 text-sm">
            © 2026 Educado AI
          </p>
        </div>
      </footer>

      {/* Polling Zone Login Modal */}
      {showPollingLoginModal && (
        <div className="fixed inset-0 bg-opacity-20 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-primary/20 relative">
            <button
              onClick={() => setShowPollingLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <HiLockClosed className="text-primary text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Login Required
              </h3>
              <p className="text-gray-600 mb-6">
                Please login or sign up to access the Polling Zone and share your opinions on trending AI topics.
              </p>
              <GoogleSignInButton variant="primary" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;